import client from "@/api/openapi-ts";
import { defineStore, acceptHMRUpdate } from "pinia";
import { ref } from "vue";
import { Geography } from "@/types/api/Geography";
import { toTitleCase } from "@/stores/helpers/helpers";
import type { components } from "@/api/schema.d.ts";

type schemas = components["schemas"];

type GeographyParams = {
  state_ids?: number[];
};

export const useGeographyStore = defineStore("geography", () => {
  const loading = ref(false);
  const loadingStates = ref(false);
  const geographies = ref<Geography[]>([]);
  const states = ref<schemas["Geography"][]>([]);
  const geographyCache = ref<Map<string, Geography[]>>(new Map());
  const loadingByType = ref<Map<string, boolean>>(new Map());
  const pendingRequests = ref<Map<string, Promise<Geography[]>>>(new Map());

  return {
    loading,
    loadingStates,
    geographies,
    states,
    async get(id: number) {
      loading.value = true;
      try {
        const { data, error } = await client.GET(
          "/api/v1/geography/{geography_id}",
          {
            params: { path: { geography_id: id } },
          },
        );
        if (error) throw error;
        const geography = data as Geography;
        if (["counties", "cities", "jurisnames"].includes(geography.type)) {
          return {
            ...geography,
            name: toTitleCase(geography.name),
          };
        }
        return geography;
      } finally {
        loading.value = false;
      }
    },
    getList(type: string, state_id?: number | number[]) {
      const cacheKey = state_id
        ? `${type}:${Array.isArray(state_id) ? [...state_id].sort().join(",") : state_id}`
        : type;

      if (geographyCache.value.has(cacheKey)) {
        geographies.value = geographyCache.value.get(cacheKey)!;
        return Promise.resolve(geographies.value);
      }

      if (pendingRequests.value.has(cacheKey)) {
        return pendingRequests.value.get(cacheKey)!.then((result) => {
          geographies.value = result;
          return result;
        });
      }

      loadingByType.value.set(cacheKey, true);
      loading.value = true;
      const params: GeographyParams = {};
      if (state_id) {
        params.state_ids = Array.isArray(state_id) ? state_id : [state_id];
      }
      const promise = client
        .GET("/api/v1/geography/{geography_type}", {
          params: { path: { geography_type: type }, query: params as never },
        })
        .then(({ data, error }) => {
          if (error) throw error;
          const sortedGeos = (data as Geography[]).sort((a: any, b: any) =>
            a.name.localeCompare(b.name),
          );
          let result: Geography[];
          if (["counties", "cities"].includes(type)) {
            result = sortedGeos.map((geo) => {
              return { ...geo, name: toTitleCase(geo.name) };
            });
          } else {
            result = sortedGeos;
          }
          geographyCache.value.set(cacheKey, result);
          geographies.value = result;
          return result;
        })
        .finally(() => {
          pendingRequests.value.delete(cacheKey);
          loadingByType.value.set(cacheKey, false);
          const hasAnyLoading = Array.from(loadingByType.value.values()).some(
            (v) => v,
          );
          loading.value = hasAnyLoading;
        });
      pendingRequests.value.set(cacheKey, promise);
      return promise;
    },
    async loadStates() {
      loadingStates.value = true;
      try {
        const response = await client.GET(
          "/api/v1/geography/{geography_type}",
          {
            params: {
              path: {
                geography_type: "states",
              },
            },
          },
        );
        states.value = (response.data as schemas["Geography"][]).sort(
          (a: any, b: any) => a.name.localeCompare(b.name),
        );
      } catch (error) {
        console.error(error);
      } finally {
        loadingStates.value = false;
      }
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useGeographyStore, import.meta.hot));
}
